import rootStore from "@/app/store";
import crud, { ApiResponse } from "../crud";

export enum OfficeFinishing {
  DRAFT = "draft",
  DONE = "done",
}

export interface Office {
  id: number;
  display_name: string;
  location_display_name: string;
  office_type: number;
  area: number;
  price: number;
  room_count: number;
  location: number;
  floor: number;
  office_lock?: number;
  commissioning_date: string;
  workplace_count: number;
  ceiling_height: number;
  description: string;
  finishing: OfficeFinishing;
  main_photo?: string;
  additional_photos: string[];
  min_period: number;
  public: boolean;
  features: {
    [key: string]: boolean;
  };
}

export interface Workplace {
  id: number;
  display_name: string;
  office_id: number;
}

export type OfficeCreate = Omit<Office, "id" | "location_display_name">;
export type OfficeUpdate = Partial<OfficeCreate>;
export type WorkplaceCreate = Omit<Workplace, "id" | "office_id">;
export type WorkplaceUpdate = Partial<WorkplaceCreate>;

export function OfficesAPI(baseUrl: string) {
  return {
    list: async () => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.get(
        `${baseUrl}/offices/?business_profile_id=${businessProfileId}`,
        undefined,
        true,
      )) as ApiResponse<Office[]>;
    },
    get: async (id: number) => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.get(
        `${baseUrl}/offices/${id}?business_profile_id=${businessProfileId}`,
        undefined,
        true,
      )) as ApiResponse<Office>;
    },
    create: async (officeData: OfficeCreate) => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.post(
        `${baseUrl}/offices/?business_profile_id=${businessProfileId}`,
        officeData,
        true,
      )) as ApiResponse<Office>;
    },
    update: async (id: number, officeData: OfficeUpdate) => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.put(
        `${baseUrl}/offices/${id}?business_profile_id=${businessProfileId}`,
        officeData,
        true,
      )) as ApiResponse<object>;
    },
    delete: async (id: number) => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.delete(
        `${baseUrl}/offices/${id}?business_profile_id=${businessProfileId}`,
        true,
      )) as ApiResponse<object>;
    },
    listPublic: async () => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.get(
        `${baseUrl}/offices/public?business_profile_id=${businessProfileId}`,
        undefined,
        true,
      )) as ApiResponse<Office[]>;
    },
    generateDescription: async (data: Office) => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.post(
        `${baseUrl}/offices/generate-ai-description?business_profile_id=${businessProfileId}`,
        data,
        true,
      )) as ApiResponse<string>;
    },
    workplaces: {
      // todo: метод list необходимо перенести в отдельную апишку для хендбуков
      list: async () => {
        const businessProfileId =
          rootStore.getState().businessProfile.businessProfile.id;
        return (await crud.get(
          `${baseUrl}/offices/workplaces?business_profile_id=${businessProfileId}`,
          undefined,
          true,
        )) as ApiResponse<Workplace[]>;
      },
      listByOffice: async (officeId: number) => {
        const businessProfileId =
          rootStore.getState().businessProfile.businessProfile.id;
        return (await crud.get(
          `${baseUrl}/offices/${officeId}/workplaces?business_profile_id=${businessProfileId}`,
          undefined,
          true,
        )) as ApiResponse<Workplace[]>;
      },
      create: async (officeId: number, workplaceData: WorkplaceCreate) => {
        const businessProfileId =
          rootStore.getState().businessProfile.businessProfile.id;
        return (await crud.post(
          `${baseUrl}/offices/${officeId}/workplaces?business_profile_id=${businessProfileId}`,
          workplaceData,
          true,
        )) as ApiResponse<Workplace>;
      },
      update: async (
        officeId: number,
        workplaceId: number,
        workplaceData: WorkplaceUpdate,
      ) => {
        const businessProfileId =
          rootStore.getState().businessProfile.businessProfile.id;
        return (await crud.put(
          `${baseUrl}/offices/${officeId}/workplaces/${workplaceId}?business_profile_id=${businessProfileId}`,
          workplaceData,
          true,
        )) as ApiResponse<Workplace>;
      },
      delete: async (officeId: number, workplaceId: number) => {
        const businessProfileId =
          rootStore.getState().businessProfile.businessProfile.id;
        return (await crud.delete(
          `${baseUrl}/offices/${officeId}/workplaces/${workplaceId}?business_profile_id=${businessProfileId}`,
          true,
        )) as ApiResponse<object>;
      },
    },
  };
}
